import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ServiceProviderCard = ({ provider, onViewProfile, onContact, viewMode = 'card' }) => {
  const getCategoryIcon = (category) => {
    const icons = {
      'Plumbing': 'Wrench',
      'Electrical': 'Zap',
      'Carpentry': 'Hammer',
      'Cleaning': 'Sparkles',
      'Babysitting': 'Baby',
      'Tutoring': 'GraduationCap',
      'Driving': 'Car',
      'Cooking': 'ChefHat',
      'Laundry': 'Shirt',
      'Errands': 'ShoppingBag',
      'Tech Support': 'Laptop',
      'Beauty Services': 'Scissors'
    };
    return icons?.[category] || 'User';
  };

  if (viewMode === 'list') {
    return (
      <div className="bg-white border border-gray-100 rounded-xl p-4 md:p-6 hover:shadow-[0_4px_16px_rgba(0,0,0,0.06)] transition-smooth">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          <div className="flex items-start gap-3 md:gap-4 flex-1 min-w-0">
            <div className="relative flex-shrink-0">
              <img
                src={provider?.image}
                alt={provider?.imageAlt || `${provider?.name} profile picture`}
                className="w-16 h-16 md:w-20 md:h-20 rounded-full object-cover border-2 border-[#FF6A00]"
              />
              {provider?.isVerified && (
                <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center border-2 border-white">
                  <Icon name="Check" size={12} color="#FF6A00" />
                </div>
              )}
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2 mb-2">
                <h3 className="text-base md:text-lg font-heading font-semibold text-foreground">
                  {provider?.name}
                </h3>
                <div className="flex items-center gap-1 bg-orange-100 px-2 py-1 rounded-md flex-shrink-0">
                  <Icon name="Star" size={14} className="text-[#FF6A00] fill-[#FF6A00]" />
                  <span className="text-sm font-semibold text-foreground">{provider?.rating}</span>
                  <span className="text-xs text-muted-foreground">({provider?.reviews})</span>
                </div>
              </div>
              
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="text-xs md:text-sm px-2.5 py-1 bg-[#1E73BE] text-white rounded-md font-medium flex items-center gap-1">
                  <Icon name={getCategoryIcon(provider?.category)} size={14} />
                  {provider?.category}
                </span>
                {provider?.skills?.slice(0, 3)?.map((skill, index) => (
                  <span key={index} className="text-xs px-2 py-1 bg-[#1E73BE]/10 text-[#1E73BE] border border-[#1E73BE]/20 rounded-md">
                    {skill}
                  </span>
                ))}
                {provider?.skills?.length > 3 && (
                  <span className="text-xs text-muted-foreground">+{provider?.skills?.length - 3} more</span>
                )}
              </div>
              
              <p className="text-sm md:text-base text-muted-foreground line-clamp-2 mb-3">
                {provider?.bio}
              </p>
              
              <div className="flex flex-wrap items-center gap-3 md:gap-4 text-xs md:text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Icon name="MapPin" size={14} color="#1E73BE" />
                  {provider?.location}
                </span>
                <span className="flex items-center gap-1">
                  <Icon name="Briefcase" size={14} />
                  {provider?.completedJobs} jobs completed
                </span>
                <span className="flex items-center gap-1 font-semibold text-[#FF6A00]">
                  <Icon name="Wallet" size={14} color="#FF6A00" />
                  ₱{provider?.hourlyRate}/hr
                </span>
              </div>
            </div>
          </div>
          
          <div className="flex lg:flex-col gap-2 lg:w-32 flex-shrink-0">
            <Button
              variant="default"
              size="sm"
              fullWidth
              onClick={() => onViewProfile?.(provider)}
              className="bg-[#1E73BE] hover:bg-[#2A85D8]"
            >
              View Profile
            </Button>
            <Button
              variant="outline"
              size="sm"
              fullWidth
              onClick={() => onContact?.(provider)}
              iconName="MessageCircle"
              className="border-[#1E73BE] text-[#1E73BE] hover:bg-[#1E73BE]/10"
            >
              Contact
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-100 rounded-lg hover:shadow-[0_4px_16px_rgba(0,0,0,0.06)] transition-smooth overflow-hidden">
      <div className="p-3">
        <div className="flex items-start gap-2 mb-2">
          <div className="relative flex-shrink-0">
            <img
              src={provider?.image}
              alt={provider?.imageAlt || `${provider?.name} profile picture`}
              className="w-12 h-12 rounded-full object-cover border-2 border-[#FF6A00]"
            />
            {provider?.isVerified && (
              <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-orange-100 rounded-full flex items-center justify-center border border-white">
                <Icon name="Check" size={8} color="#FF6A00" />
              </div>
            )}
          </div>
          
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-heading font-semibold text-foreground line-clamp-1 mb-1">
              {provider?.name}
            </h3>
            <div className="flex items-center gap-1 mb-1">
              <Icon name="Star" size={12} className="text-[#FF6A00] fill-[#FF6A00]" />
              <span className="text-xs font-semibold text-foreground">{provider?.rating}</span>
              <span className="text-xs text-muted-foreground">({provider?.reviews})</span>
            </div>
            <span className="text-xs px-1.5 py-0.5 bg-[#1E73BE] text-white rounded inline-flex items-center gap-1">
              <Icon name={getCategoryIcon(provider?.category)} size={10} />
              {provider?.category}
            </span>
          </div>
        </div>
        
        <p className="text-xs text-muted-foreground line-clamp-2 mb-2 leading-relaxed">
          {provider?.bio}
        </p>
        
        <div className="flex flex-wrap gap-1 mb-2">
          {provider?.skills?.slice(0, 2)?.map((skill, index) => (
            <span key={index} className="text-xs px-1.5 py-0.5 bg-[#1E73BE]/10 text-[#1E73BE] border border-[#1E73BE]/20 rounded">
              {skill}
            </span>
          ))}
          {provider?.skills?.length > 2 && (
            <span className="text-xs text-muted-foreground">+{provider?.skills?.length - 2}</span>
          )}
        </div>
        
        <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
          <span className="flex items-center gap-1">
            <Icon name="MapPin" size={12} color="#1E73BE" />
            <span className="truncate">{provider?.location}</span>
          </span>
          <span className="flex items-center gap-1">
            <Icon name="Briefcase" size={12} />
            {provider?.completedJobs}
          </span>
        </div>
        
        <div className="flex items-center justify-between pt-2 border-t border-gray-100">
          <div>
            <span className="text-xs text-muted-foreground block">Rate</span>
            <span className="text-sm font-semibold text-[#FF6A00]">
              ₱{provider?.hourlyRate}/hr
            </span>
          </div>
          <div className="flex gap-1.5">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onContact?.(provider)}
              className="text-xs px-2 py-1.5 h-auto border-[#1E73BE] text-[#1E73BE]"
            >
              <Icon name="MessageCircle" size={12} />
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={() => onViewProfile?.(provider)}
              className="bg-[#1E73BE] hover:bg-[#2A85D8] text-xs px-3 py-1.5 h-auto"
            >
              View
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceProviderCard;